import { useMutation } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";

/** Wire shape of one call to `POST /shadow/plausibility/reverify`. */
export interface ReverifyReport {
	dryRun: boolean;
	endCursor?: string;
	enqueued: number;
	failed: number;
	hasMore: boolean;
	mode: string;
	remaining: number;
	/** The server stopped the run; see `ReverifyReport.stopped` in services. */
	stopped?: string;
}

/** Running totals across batches. */
export interface ReverifyTotals {
	dryRun: boolean;
	enqueued: number;
	failed: number;
	mode: string;
	remaining: number;
}

/**
 * Re-verify the selected organization's text-only down-ranks. A dry run is one
 * call that counts them. A run walks the rows a batch per request (each stays
 * inside API Gateway's limit) until the cursor runs out. A row that keeps
 * failing is passed over, so the run ends; the next run retries it. When the
 * server reports the run stopped (EventBridge or the database failing), the
 * totals so far are reported and the mutation fails with the reason.
 */
export const useReverify = (onProgress?: (totals: ReverifyTotals) => void) =>
	useMutation({
		mutationFn: async (input: { dryRun: boolean }) => {
			const call = async (after?: string) =>
				(
					await apiClient.post<ReverifyReport>(
						ENDPOINTS.shadow.plausibility.reverify,
						{ ...input, after },
					)
				).data;

			let report = await call();
			const totals: ReverifyTotals = {
				dryRun: report.dryRun,
				enqueued: report.enqueued,
				failed: report.failed,
				mode: report.mode,
				remaining: report.remaining,
			};
			onProgress?.({ ...totals });
			while (
				!input.dryRun &&
				!report.stopped &&
				report.hasMore &&
				report.endCursor
			) {
				report = await call(report.endCursor);
				totals.enqueued += report.enqueued;
				totals.failed += report.failed;
				totals.remaining = report.remaining;
				onProgress?.({ ...totals });
			}
			if (report.stopped) {
				throw new Error(
					`Stopped early: ${report.stopped}. Run again once it clears to retry what is left.`,
				);
			}
			return totals;
		},
	});
